import * as React from 'react';

import type { IntegrationsApi } from '../api';
import type { Integration, IntegrationList, IntegrationRemoval, TenantRef } from '../integrationTypes';

/** What every card of the page shares: the calls bound to the shown tenant, the list's permissions and
 *  environment, and how a change lands (`replace` with the connection a write answered, `reload` after a
 *  removal or an add). */
export interface IntegrationsPageContext {
    api: IntegrationsApi;
    environment: IntegrationList['environment'];
    permissions: IntegrationList['permissions'];
    tenant: TenantRef;
    /** `metrics.tracked_since`: before it nothing was recorded ("none since", never "never"); null: unknown. */
    trackedSince: string | null;
    replace: (connection: Integration) => void;
    reload: () => Promise<void>;
    /** A removal answered 200: its note stays on the page until dismissed (what to do at the vendor). */
    noteRemoval: (what: string, note: IntegrationRemoval | null) => void;
}

export const IntegrationsContext = React.createContext<IntegrationsPageContext | null>(null);

export const useIntegrations = (): IntegrationsPageContext => {
    const value = React.useContext(IntegrationsContext);
    if (!value) {
        throw new Error('useIntegrations outside the Integrations page');
    }
    return value;
};

export type Outcome<T> = { ok: true; value: T } | { ok: false; error: unknown };

/** Run one call: `busy` while it runs, its refusal kept in `error` (shown by the caller). */
export function useAction() {
    const [busy, setBusy] = React.useState(false);
    const [error, setError] = React.useState<unknown>(null);
    const mounted = React.useRef(true);
    React.useEffect(() => () => { mounted.current = false; }, []);
    const run = React.useCallback(async <T,>(fn: () => Promise<T>): Promise<Outcome<T>> => {
        setBusy(true);
        setError(null);
        try {
            return { ok: true, value: await fn() };
        } catch (e) {
            if (mounted.current) {
                setError(e);
            }
            return { ok: false, error: e };
        } finally {
            if (mounted.current) {
                setBusy(false);
            }
        }
    }, []);
    return { busy, error, setError, run };
}
